import {Helmet} from '@dr.pogodin/react-helmet';
import {isFeatureEnabled} from 'feature-flags';
import {
  BaseFlagProps,
  FlagProps,
  openConfirmDialog,
  openDeleteDialog,
  showNotification,
  AlertProps,
} from 'platform/components';
import {HStack, Show, Space, Text} from 'platform/foundation';
import {useDateTimeFormatter, useFormatCurrency} from 'platform/locale';
import {match} from 'ts-pattern';

import {Fragment, ReactNode} from 'react';
import {useDispatch} from 'react-redux';

import {always, defaultTo, equals, isNil, not} from 'ramda';
import {concatAll, isFalsy, isNilOrEmpty, isPositive} from 'ramda-adjunct';

import {
  documentContextApi,
  useGetBranchListQuery,
  useGetDocumentContextCountQuery,
  useGetTenantQuery,
} from '@dms/api/core';
import {useGetUserQuery} from '@dms/api/customer';
import {useGetAuthorizationProfilesQuery} from '@dms/api/security';
import {EntityResourceIds} from '@dms/api/shared';
import {
  GetDirectSaleResponse,
  GetWarehousesResponse,
  useGetDirectSaleQuery,
  useGetDirectSaleTypesQuery,
  useGetDirectSaleVariantQuery,
  useGetMaterialPriceTypesQuery,
  useGetWarehouseHeaderDynamicActionsQuery,
  useGetWarehousesQuery,
  useGetWorkRateTypesQuery,
  usePatchDirectSaleMutation,
  usePostPrintoutOnDemandMutation,
  usePutWarehouseHeaderDynamicActionsMutation,
} from '@dms/api/warehouse';
import {featureFlags} from '@dms/feature-flags';
import {TaskGridPage, useTasksCount} from '@dms/features/tasks';
import i18n from '@dms/i18n';
import {testIds, warehouseRoutes} from '@dms/routes';
import {
  CheckoutIssuingProvider,
  Checklists,
  DetailTemplate,
  DetailTemplateHeader,
  getUserName,
  handleApiError,
  NavigationItem,
  queryParams,
  createHeaderActions,
  Section,
  useChecklist,
  usePermissions,
  printPDF,
  refreshDmsDatagrid,
  WAREHOUSE_GRID_CODES,
} from '@dms/shared';
import {createHeaderFlags} from '@dms/warehouse/shared';
import {useWarehouseParams} from '@dms/warehouse/shared-components';

import {
  buildArray,
  composePath,
  EMPTY_PLACEHOLDER,
  generateHashFromObjects,
  getRandomId,
  Nullish,
  parseDate,
  useBoolean,
  useNavigate,
  useQueryState,
} from 'shared/web';

import {Customer} from './(sections)/Customer/Customer';
import {Overview} from './(sections)/DirectSale/Overview';
import {TABS_IDS} from './(sections)/DirectSale/utils/tabIds';
import {Documents} from './(sections)/Documents/Documents';
import {Vehicle} from './(sections)/Vehicle/Vehicle';
import {DirectSaleMaterialReturnModal} from './components/DirectSaleMaterialReturnModal';

const DIRECT_SALE_SECTION_IDS = {
  directSale: 'directSale',
  documents: 'documents',
  vehicle: 'vehicle',
  checklists: 'checklists',
};

const PERCENTAGE_BASE = 100;

export function DirectSalesDetail() {
  const {directSaleId} = useWarehouseParams();

  const navigate = useNavigate();

  const currencyFormatter = useFormatCurrency();

  const formatDate = useDateTimeFormatter();

  const dispatch = useDispatch();

  const [activeSectionId, setActiveSectionId] = useQueryState(
    queryParams.COMPONENT_SECTIONS_SECTION
  );

  const [, setActiveTabId] = useQueryState(queryParams.COMPONENT_SECTIONS_TAB);

  const [isMaterialReturnModalVisible, openMaterialReturnModal, closeMaterialReturnModal] =
    useBoolean();

  const [directSaleEdit, canReadTasks] = usePermissions({
    permissionKeys: ['directSaleEdit', 'readTask'],
  });

  const {count: tasksTodoCount} = useTasksCount({
    entityId: directSaleId,
    relation: EntityResourceIds.directSale,
    state: 'TODO',
  });

  const {count: tasksInProgressCount} = useTasksCount({
    entityId: directSaleId,
    relation: EntityResourceIds.directSale,
    state: 'IN_PROGRESS',
  });

  const tasksCountBadge = tasksTodoCount + tasksInProgressCount;

  const {
    data: directSale,
    isLoading: isDirectSaleLoading,
    isFetching: isDirectSaleFetching,
    isError: hasDirectSaleError,
  } = useGetDirectSaleQuery(
    {directSaleId: directSaleId as string},
    {skip: isNilOrEmpty(directSaleId)}
  );

  const {
    data: availableWarehouses,
    isLoading: isAvailableWarehousesLoading,
    isError: isAvailableWarehousesError,
  } = useGetWarehousesQuery(
    {directSaleVariantId: defaultTo('', directSale?.directSaleVariantId)},
    {skip: isNil(directSale)}
  );

  const {
    data: directSaleVariant,
    isLoading: isDirectSaleVariantLoading,
    isError: hasDirectSaleVariantError,
  } = useGetDirectSaleVariantQuery(
    {directSaleVariantId: defaultTo('', directSale?.directSaleVariantId)},
    {skip: isNil(directSale)}
  );

  const {data: user, isLoading: isUserLoading} = useGetUserQuery({id: directSale?.createdBy ?? ''});

  const {
    data: directSaleActions,
    isLoading: isDirectSaleActionsLoading,
    isError: hasDirectSaleActionsError,
  } = useGetWarehouseHeaderDynamicActionsQuery({id: directSaleId, resource: 'direct-sale'});

  const {data: tenant, isLoading: isTenantLoading, isError: hasTenantError} = useGetTenantQuery();

  const {
    data: authorizationProfiles,
    isLoading: isAuthorizationProfilesLoading,
    isError: hasAuthorizationProfilesError,
  } = useGetAuthorizationProfilesQuery({'x-tenant': tenant?.id ?? ''}, {skip: isNil(tenant)});

  const {
    data: branches,
    isLoading: areBranchesLoading,
    isError: hasBranchesError,
  } = useGetBranchListQuery();

  const {
    data: directSaleTypes,
    isLoading: isDirectSaleTypesLoading,
    isError: hasDirectSaleTypesError,
  } = useGetDirectSaleTypesQuery();

  const {
    data: materialPriceTypes,
    isLoading: isMaterialPriceTypesLoading,
    isError: hasMaterialPriceTypesError,
  } = useGetMaterialPriceTypesQuery();

  const {
    data: labourRateTypes,
    isLoading: isLabourRateTypesLoading,
    isError: hasLabourRateTypesError,
  } = useGetWorkRateTypesQuery();

  const documentCountQuery = useGetDocumentContextCountQuery(
    {contextTarget: 'direct-sale', contextId: directSaleId},
    {skip: !directSaleId}
  );

  const [saveDirectSale] = usePatchDirectSaleMutation();

  const [putDirectSaleAction, {isLoading: isPutDirectSaleActionLoading}] =
    usePutWarehouseHeaderDynamicActionsMutation();

  const [postPrintoutOnDemand, {isLoading: isPostPrintoutOnDemandLoading}] =
    usePostPrintoutOnDemandMutation();

  const invalidateDocumentsCount = () => {
    // We need to wait for the document count to be updated through kafka between metada and php
    setTimeout(() => {
      dispatch(documentContextApi.util.invalidateTags(['documentsCount']));
    }, 1000);
  };

  const isLoading =
    isDirectSaleLoading ||
    isDirectSaleVariantLoading ||
    isUserLoading ||
    isAvailableWarehousesLoading ||
    isDirectSaleActionsLoading ||
    isTenantLoading ||
    isAuthorizationProfilesLoading ||
    areBranchesLoading ||
    isDirectSaleTypesLoading ||
    isMaterialPriceTypesLoading ||
    isLabourRateTypesLoading;

  const isError =
    hasDirectSaleError ||
    hasDirectSaleVariantError ||
    hasDirectSaleActionsError ||
    isAvailableWarehousesError ||
    hasTenantError ||
    hasAuthorizationProfilesError ||
    hasBranchesError ||
    hasDirectSaleTypesError ||
    hasMaterialPriceTypesError ||
    hasLabourRateTypesError;

  const directSaleTitle = `${i18n.t('page.warehouse.labels.directSale')} ${
    directSale?.number ?? ''
  }`;

  const priceWithVat = currencyFormatter(
    directSale?.totalPrice.withVat.amount ?? 0,
    directSale?.totalPrice.withVat.currency ?? '',
    2
  );

  const priceWithoutVat = currencyFormatter(
    directSale?.totalPrice.withoutVat.amount ?? 0,
    directSale?.totalPrice.withoutVat.currency ?? '',
    2
  );

  const hasPendingReturn = !!directSale?.hasPendingReturn;

  const formatToShortDate = (date?: string) => {
    if (isNil(date)) {
      return EMPTY_PLACEHOLDER;
    }
    return formatDate('dateShort', parseDate(date));
  };

  const createdAt = `${i18n.t('general.labels.dateCreated')}: ${formatToShortDate(directSale?.createdAt)}`;

  const createdBy = `${i18n.t('general.labels.createdBy')}: ${getUserName(user) ?? EMPTY_PLACEHOLDER}`;

  const isLockedOrClosed = directSale?.state === 'LOCKED' || directSale?.state === 'CLOSED';

  const handleChangeDirectSaleVariant = () => {
    navigate(
      composePath(warehouseRoutes.directSalesVariantChange, {
        params: {id: directSaleId, directSaleVariantId: directSale?.directSaleVariantId},
      })
    );
  };

  const handleActionClick = (actionKey: string) => {
    if (actionKey === 'prntDirectSaleOffer') {
      return postPrintoutOnDemand({
        body: {
          docOriginId: directSaleId,
          originEntityPrintoutType: 'prntDirectSaleOffer',
        },
      })
        .unwrap()
        .then((data) => {
          const pdfUrl = data?.printout?.pdfUrl;

          if (pdfUrl) {
            printPDF(pdfUrl);
          } else {
            showNotification.error();
          }
        })
        .then(() => documentCountQuery.refetch())
        .then(() =>
          showNotification.success(i18n.t('entity.warehouse.labels.documentWasGenerated'))
        )
        .then(() => {
          navigate(
            composePath(warehouseRoutes.directSalesDetailDocuments, {
              params: {id: directSaleId},
            })
          );
        })
        .catch(handleApiError);
    }

    match(actionKey)
      .with('return', () => openMaterialReturnModal())
      .with('discount', () => {
        setActiveTabId(TABS_IDS.discountTab);
      })
      .with('variantChange', () => handleChangeDirectSaleVariant())
      .with('delete', () =>
        openDeleteDialog({
          onConfirm: () =>
            putDirectSaleAction({id: directSaleId, resource: 'direct-sale', body: {actionKey}})
              .unwrap()
              .then(() => navigate(composePath(warehouseRoutes.directSalesList)))
              .catch(handleApiError),
          'data-testid': testIds.warehouse.directSalesDetailOverview('delete'),
        })
      )
      .with('offer', () =>
        putDirectSaleAction({id: directSaleId, resource: 'direct-sale', body: {actionKey}})
          .unwrap()
          .then(invalidateDocumentsCount)
          .catch(handleApiError)
      )
      .otherwise(() => {
        const confirmText = match(actionKey)
          .with(
            'lock',
            always(`${i18n.t('page.warehouse.labels.lockDirectSale')} ${directSale?.number}?`)
          )
          .otherwise(always(null));

        if (isNil(confirmText)) {
          return putDirectSaleAction({
            id: directSaleId,
            resource: 'direct-sale',
            body: {actionKey},
          })
            .unwrap()
            .catch(handleApiError);
        }

        openConfirmDialog({
          text: confirmText,
          onConfirm: () =>
            putDirectSaleAction({id: directSaleId, resource: 'direct-sale', body: {actionKey}})
              .unwrap()
              .catch(handleApiError),
          'data-testid': testIds.warehouse.directSalesDetailOverview('dialogs'),
        });
      });
  };

  const handleVehicleChange = (vehicleId: string | Nullish) =>
    saveDirectSale({directSaleId, body: {vehicleId}})
      .unwrap()
      .then(() =>
        showNotification.success(
          isNil(vehicleId)
            ? i18n.t('entity.warehouse.notifications.vehicleRemoved')
            : i18n.t('entity.warehouse.notifications.vehicleAssigned')
        )
      )
      .catch(handleApiError);

  const handleCustomerChange = (customerId: string | Nullish) =>
    saveDirectSale({directSaleId, body: {customerId}})
      .unwrap()
      .then(() =>
        showNotification.success(
          isNil(customerId)
            ? i18n.t('entity.warehouse.notifications.customerRemoved')
            : i18n.t('entity.warehouse.notifications.customerAssigned')
        )
      )
      .catch(handleApiError);

  const handleCloseMaterialReturnModal = () => {
    closeMaterialReturnModal();
    refreshDmsDatagrid(WAREHOUSE_GRID_CODES.warehouseDirectSaleItem);
  };

  const getAuthorizationProfileFromVariant = (authorizationProfileId: string | Nullish) => {
    const result = authorizationProfiles?.find((authorizationProfile) =>
      equals(authorizationProfile.id, authorizationProfileId)
    );

    return defaultTo(EMPTY_PLACEHOLDER, result?.name);
  };

  const getBranchFromDirectSale = (branchId: string | Nullish) => {
    const result = branches?.branchListItems?.find((branch) => equals(branch.id, branchId));

    return defaultTo(EMPTY_PLACEHOLDER, result?.marketingName);
  };

  const getDirectSaleTypeFromVariant = (directSaleTypeId: string | Nullish) => {
    const result = directSaleTypes?.find((directSaleType) =>
      equals(directSaleType?.directSaleTypeId, directSaleTypeId)
    );

    return defaultTo(EMPTY_PLACEHOLDER, result?.name);
  };

  const getPriceTypeFromVariant = (code: string | Nullish, type: 'material' | 'labour') =>
    match(type)
      .with('material', () => {
        const result = materialPriceTypes?.find((materialPriceType) =>
          equals(materialPriceType?.code, code)
        );
        return defaultTo(EMPTY_PLACEHOLDER, result?.name);
      })
      .with('labour', () => {
        const result = labourRateTypes?.find((labourRateType) =>
          equals(labourRateType?.code, code)
        );
        return defaultTo(EMPTY_PLACEHOLDER, result?.name);
      })
      .exhaustive();

  const getMarginFromVariant = (margin: number | Nullish) =>
    margin ? `${margin * PERCENTAGE_BASE} %` : EMPTY_PLACEHOLDER;

  const getRatioFromVariant = (ratio: number | Nullish) =>
    ratio ? ratio * PERCENTAGE_BASE : EMPTY_PLACEHOLDER;

  const dynamicFlags = createHeaderFlags(directSale?.markers);

  const staticFlags = match(directSale?.state)
    .returnType<Omit<BaseFlagProps, 'size'>[]>()
    .with(
      'LOCKED',
      always([
        {
          colorScheme: 'green',
          label: i18n.t('entity.warehouse.labels.lockedState'),
        },
      ])
    )
    .with(
      'CLOSED',
      always([
        {
          colorScheme: 'green',
          label: i18n.t('entity.warehouse.labels.closedState'),
        },
      ])
    )
    .otherwise(
      always([
        {
          colorScheme: 'blue',
          label: i18n.t('entity.warehouse.labels.openState'),
        },
      ])
    );

  const headerFlags = concatAll<FlagProps[]>([staticFlags, dynamicFlags]);

  const headerActions = createHeaderActions({
    actions: directSaleActions?.actions,
    callback: handleActionClick,
    isLoading: isPutDirectSaleActionLoading || isPostPrintoutOnDemandLoading,
  });

  const variantTooltipData = [
    {
      label: i18n.t('entity.warehouse.labels.authorizationProfile'),
      value: getAuthorizationProfileFromVariant(directSaleVariant?.general?.authorizationProfileId),
    },
    {
      label: i18n.t('entity.warehouse.labels.branch'),
      value: getBranchFromDirectSale(directSale?.branchId),
    },
    {
      label: i18n.t('entity.warehouse.labels.directSaleType'),
      value: getDirectSaleTypeFromVariant(directSaleVariant?.general?.directSaleTypeId),
    },
    {
      label: i18n.t('entity.warehouse.labels.unitPriceWithVAT'),
      value: isFalsy(directSaleVariant?.general?.isUnitPriceWithVat)
        ? i18n.t('general.labels.no')
        : i18n.t('general.labels.yes'),
    },
    {
      label: i18n.t('entity.warehouse.labels.materialPriceType'),
      value: getPriceTypeFromVariant(directSaleVariant?.general?.priceType, 'material'),
    },
    {
      label: i18n.t('entity.warehouse.labels.minimumMaterialMargin'),
      value: getMarginFromVariant(directSaleVariant?.general?.minimalMaterialMargin),
    },
    {
      label: i18n.t('entity.warehouse.labels.materialPriceTypeRatio'),
      value: getRatioFromVariant(directSaleVariant?.general?.priceTypeRatio),
    },
    {
      label: i18n.t('entity.warehouse.labels.labourRateType'),
      value: getPriceTypeFromVariant(directSaleVariant?.general?.rateType, 'labour'),
    },
    {
      label: i18n.t('entity.warehouse.labels.minimumLabourMargin'),
      value: getMarginFromVariant(directSaleVariant?.general?.minimalWorkMargin),
    },
    {
      label: i18n.t('entity.warehouse.labels.labourRatio'),
      value: getRatioFromVariant(directSaleVariant?.general?.rateTypeRatio),
    },
    {
      label: i18n.t('entity.warehouse.labels.note'),
      value: defaultTo(EMPTY_PLACEHOLDER, directSaleVariant?.general?.note),
    },
  ];

  const variantTooltipContent: ReactNode = (
    <>
      {variantTooltipData.map((tooltipItem, index) => {
        const isFirstItem = index === 0;

        return (
          <Fragment key={`tooltipItem-${getRandomId()}`}>
            {not(isFirstItem) && <Space vertical={1} />}

            <HStack spacing={1}>
              <Text size="xSmall" color="white">
                {`• ${tooltipItem.label}:`}
              </Text>
              <Text size="xSmall" color="white" alternative>
                {tooltipItem.value}
              </Text>
            </HStack>
          </Fragment>
        );
      })}
    </>
  );

  const header: DetailTemplateHeader = {
    title: directSaleTitle,
    isEntityLoading: isDirectSaleFetching,
    icon: 'action/shopping_cart',
    parameters: [
      directSale?.number,
      {
        title: directSale?.variantName,
        tooltip: variantTooltipContent,
        shouldShowTooltipIcon: true,
        hasAutoWidth: true,
      },
      createdAt,
      createdBy,
    ],
    recordId: directSale?.id,
    resourceId: EntityResourceIds.directSales,
    controls: ['ASSIGNEE'],
    flags: headerFlags,
    actions: headerActions,
    primaryParameter: defaultTo(undefined, priceWithVat),
    secondaryParameter: `${priceWithoutVat} ${i18n.t('general.labels.w/oVat')}`,
    alerts: buildArray<AlertProps>()
      .when(hasPendingReturn, {
        title: i18n.t('entity.warehouse.notifications.youHavePendingItemsForReturn'),
        hyperlinks: [
          {
            title: i18n.t('entity.warehouse.actions.showPending'),
            onClick: openMaterialReturnModal,
          },
        ],
      })
      .whenNot(directSaleEdit, {
        type: 'banner',
        variant: 'info',
        message: i18n.t('module.warehouse.label.missingDirectSaleEditPermission'),
      })
      .when(isPositive(directSale?.marginUnderMinimumCount), {
        type: 'banner',
        variant: 'warning',
        message: i18n.t('entity.warehouse.labels.marginUnderMinimumWarning'),
      }),
  };

  const {badgeSettings, checklistSettings} = useChecklist({
    recordId: directSaleId || '',
    resourceId: 'DIRECT_SALE',
  });

  const navigation = buildArray<NavigationItem>([
    {
      id: DIRECT_SALE_SECTION_IDS.directSale,
      label: i18n.t('page.warehouse.labels.overview'),
      href: composePath(warehouseRoutes.directSalesDetailOverview, {
        params: {id: directSaleId},
      }),
      content: (
        <Overview
          directSale={directSale as GetDirectSaleResponse}
          isDirectSaleLoading={isDirectSaleLoading}
          isEditingDisabled={isLockedOrClosed}
          directSaleActions={directSaleActions?.actions}
          availableWarehouses={availableWarehouses as GetWarehousesResponse}
          onMaterialReturnModalOpen={openMaterialReturnModal}
          data-testid={testIds.warehouse.directSalesDetailOverview('section.directSale')}
        />
      ),
      'data-testid': testIds.warehouse.directSalesDetailOverview('navigation.directSale'),
    },
    {
      id: DIRECT_SALE_SECTION_IDS.documents,
      label: i18n.t('page.warehouse.labels.documents'),
      href: composePath(warehouseRoutes.directSalesDetailDocuments, {
        params: {id: directSaleId},
      }),
      content: (
        <Documents
          onDocumentAction={() => documentCountQuery.refetch()}
          data-testid={testIds.warehouse.directSalesDetailDocuments('section.documents')}
        />
      ),
      badge: !!documentCountQuery.data?.count
        ? {value: documentCountQuery.data.count, colorScheme: 'neutral'}
        : undefined,
      'data-testid': testIds.warehouse.directSalesDetailDocuments('navigation.documents'),
    },
    {
      id: DIRECT_SALE_SECTION_IDS.vehicle,
      label: i18n.t('page.warehouse.labels.vehicle'),
      href: composePath(warehouseRoutes.directSalesDetailVehicle, {
        params: {id: directSaleId},
      }),
      content: (
        <Vehicle
          vehicleId={defaultTo('', directSale?.vehicleId)}
          isDirectSaleLoading={isDirectSaleLoading}
          isEditingDisabled={isLockedOrClosed}
          onVehicleChange={handleVehicleChange}
          data-testid={testIds.warehouse.directSalesDetailVehicle('section.vehicle')}
        />
      ),
      'data-testid': testIds.warehouse.directSalesDetailVehicle('navigation.vehicle'),
    },
    {
      id: 'customer',
      label: i18n.t('page.warehouse.labels.customer'),
      href: composePath(warehouseRoutes.directSalesDetailCustomer, {
        params: {id: directSaleId},
      }),
      content: (
        <Customer
          customerId={defaultTo('', directSale?.customerId)}
          isDirectSaleLoading={isDirectSaleLoading}
          isEditingDisabled={isLockedOrClosed}
          onCustomerChange={handleCustomerChange}
          data-testid={testIds.warehouse.directSalesDetailCustomer('section.customer')}
        />
      ),
      'data-testid': testIds.warehouse.directSalesDetailCustomer('navigation.customer'),
    },
  ])
    .whenFeatureEnabled(featureFlags.SALE_TASKS_CHECKLIST, {
      id: DIRECT_SALE_SECTION_IDS.checklists,
      label: i18n.t('entity.checklist.labels.title'),
      href: composePath(warehouseRoutes.directSalesDetailChecklists, {
        params: {id: directSaleId},
      }),
      content: (
        <Section>
          <Checklists {...checklistSettings} contextTarget="direct-sale" contextId={directSaleId} />
        </Section>
      ),
      badge: badgeSettings,
      'data-testid': testIds.warehouse.directSalesDetailChecklists('checklists'),
    })
    .when(isFeatureEnabled(featureFlags.CORE_TASK_MANAGEMENT) && canReadTasks, {
      id: 'tasks',
      label: i18n.t('entity.task.labels.tasks'),
      href: composePath(warehouseRoutes.directSalesDetailTasks, {
        params: {id: directSaleId},
      }),
      'data-testid': testIds.warehouse.directSalesDetailOverview('navigation-tasks'),
      badge: {
        colorScheme: 'neutral',
        value: tasksCountBadge,
      },
      content: (
        <TaskGridPage
          gridCode="direct-sale-task"
          entityId={directSaleId}
          data-testid={testIds.warehouse.directSalesDetailOverview('tasks')}
        />
      ),
    });
  return (
    <CheckoutIssuingProvider>
      <>
        <Helmet title={i18n.t('page.warehouse.labels.directSaleDetail')} />
        <DetailTemplate
          key={generateHashFromObjects(directSale)}
          isLoading={isLoading}
          isError={isError}
          isLocked={isPutDirectSaleActionLoading}
          header={header}
          navigation={navigation}
          data-testid={testIds.warehouse.directSalesDetailOverview('page')}
          activeSectionId={activeSectionId}
          onSectionChange={setActiveSectionId}
        />

        <Show when={isMaterialReturnModalVisible}>
          <DirectSaleMaterialReturnModal
            directSaleId={directSaleId}
            authorizationProfileId={directSale?.authorizationProfileId}
            onClose={handleCloseMaterialReturnModal}
            data-testid={testIds.warehouse.directSalesDetailOverview('dialogs.returnMaterial')}
          />
        </Show>
      </>
    </CheckoutIssuingProvider>
  );
}
